import {
  AmbientLight,
  Box3,
  BufferGeometry,
  Camera,
  DirectionalLight,
  DoubleSide,
  Mesh,
  MeshLambertMaterial,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import { buildIfcGeometry, loadIfcMesh } from "./ifcMeshCache";

// IFC サムネイル用の共有 (シングルトン) WebGL レンダラ。
//
// 目的: サムネイルごとに WebGLRenderer を生成すると WebGL コンテキスト数が
// ブラウザ上限 (~8〜16) を超え、
//  - 古いコンテキストがロストして一部サムネイルが描画されない
//  - メインビューア (viewer) のコンテキストまで奪われ描画ループが乱れる
// という問題が起きていた。
//
// ここでは 1 つのオフスクリーンレンダラだけを使い、各サムネイルのシーンを
// 描画してから 2D キャンバスに drawImage で転写する。これにより WebGL
// コンテキスト数は「メインビューア + サムネイル共有の 1 個」だけになる。

let renderer: WebGLRenderer | null = null;

function getRenderer(): WebGLRenderer {
  if (!renderer) {
    renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
      // render 直後に drawImage で読み出すため描画バッファを保持する。
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
  }
  return renderer;
}

/**
 * 共有レンダラで scene/camera を描画し、結果を対象の 2D キャンバスへ転写する。
 * ドラッグ中の再描画もこれを呼ぶだけでよい (新規コンテキストを作らない)。
 */
export function renderThumbnail(
  target: HTMLCanvasElement,
  scene: Scene,
  camera: Camera,
  size: number,
): void {
  const r = getRenderer();
  const dpr = window.devicePixelRatio || 1;
  r.setSize(size, size, false);
  r.render(scene, camera);

  const ctx = target.getContext("2d");
  if (!ctx) return;
  const px = Math.floor(size * dpr);
  if (target.width !== px || target.height !== px) {
    target.width = px;
    target.height = px;
  }
  ctx.clearRect(0, 0, target.width, target.height);
  ctx.drawImage(r.domElement, 0, 0, target.width, target.height);
}

// 初回の重い処理 (ジオメトリ構築 + 初回描画) を 1 フレームに 1 件ずつ実行する
// ための簡易キュー。多数のサムネイルが同時に準備完了してもメインスレッドの
// 1 フレームに処理が集中しないようにする。
const queue: Array<() => void> = [];
let pumping = false;

function pump(): void {
  pumping = true;
  requestAnimationFrame(() => {
    const task = queue.shift();
    if (task) task();
    if (queue.length > 0) pump();
    else pumping = false;
  });
}

/**
 * 重い初期化タスクをキューに積む。返り値を呼ぶとキャンセルできる
 * (まだ実行されていなければスキップされる)。
 */
export function enqueueThumbnailTask(task: () => void): () => void {
  let cancelled = false;
  const wrapped = () => {
    if (!cancelled) task();
  };
  queue.push(wrapped);
  if (!pumping) pump();
  return () => {
    cancelled = true;
  };
}

// ---- 静的画像サムネイル (url+size ごとに 1 度だけレンダリングして PNG をキャッシュ) ----
// 1000 件のセルごとにライブ 3D canvas を持つと重いので、同一 IFC は 1 度だけ描画して
// dataURL 化し、全セルは <img> で共有表示する。IFC パースは loadIfcMesh (worker) のまま。
const imageCache = new Map<string, Promise<string>>();

function buildThumbScene(
  geometry: BufferGeometry,
  fov = 45,
): { scene: Scene; camera: PerspectiveCamera } {
  geometry.computeBoundingBox();
  const bbox = geometry.boundingBox ?? new Box3();
  const center = bbox.getCenter(new Vector3());
  const dim = bbox.getSize(new Vector3());
  geometry.translate(-center.x, -center.y, -center.z);

  const scene = new Scene();
  scene.add(new AmbientLight(0xffffff, 2));
  const dir = new DirectionalLight(0xffffff, 1.2);
  dir.position.set(1, 1, 1);
  scene.add(dir);

  const hasColor = geometry.hasAttribute("color");
  const material = new MeshLambertMaterial({
    vertexColors: hasColor,
    color: hasColor ? 0xffffff : 0xb0b0b0,
    side: DoubleSide,
  });
  scene.add(new Mesh(geometry, material));

  const radius = dim.length() / 2 || 1;
  const dist = (radius * 1.08) / Math.sin((fov * Math.PI) / 360);
  const camera = new PerspectiveCamera(
    fov,
    1,
    Math.max(dist - radius * 2, dist * 0.01),
    dist + radius * 2,
  );
  camera.position.copy(
    new Vector3(1, -1, 0.8).normalize().multiplyScalar(dist),
  );
  camera.up.set(0, 0, 1);
  camera.lookAt(0, 0, 0);
  return { scene, camera };
}

/**
 * IFC の静的サムネイル PNG (dataURL) を返す。url+size ごとに 1 度だけレンダリング
 * してキャッシュ。全セルはこの画像を共有表示するため 3D canvas を大量に持たない。
 */
export function getThumbnailImage(url: string, size = 120): Promise<string> {
  const key = `${url}@${size}`;
  const hit = imageCache.get(key);
  if (hit) return hit;
  const px = Math.max(64, Math.floor(size)) * 2; // 2x でクッキリ
  const p = loadIfcMesh(url).then(
    (meshData) =>
      new Promise<string>((resolve, reject) => {
        enqueueThumbnailTask(() => {
          try {
            const { scene, camera } = buildThumbScene(
              buildIfcGeometry(meshData),
            );
            const r = getRenderer();
            r.setSize(px, px, false);
            r.render(scene, camera);
            resolve(r.domElement.toDataURL("image/png"));
          } catch (e) {
            reject(e);
          }
        });
      }),
  );
  imageCache.set(key, p);
  return p;
}
